import { NarrowFulfilmentMethodInputType } from '@src/graphql-types'
import { LocationType } from '@src/graphql-types'
import { fulfilmentFilterReactiveVar } from '@src/hooks/useFulfilmentFilter/utils/reactiveVar'

import { calculateCollectionPreorderDateTime } from './calculateCollectionPreorderDateTime'
import { calculateDeliveryPreorderWindow } from './calculateDeliveryPreorderWindow'

import { OutletFulfilmentContextData } from '../context/types'
import { NonBasketOutletFulfilment, CurrentFulfilmentType } from '../validation'
import { useFulfilmentFilter } from '../../../useFulfilmentFilter/useFulfilmentFilter'

export const getInitialOutletFulfilment = ({
  outlet,
  fulfilmentFilterData,
}: {
  outlet: OutletFulfilmentContextData
  fulfilmentFilterData: ReturnType<typeof useFulfilmentFilter>
}): NonBasketOutletFulfilment => {
  // get current fulfilment filter state
  const fulfilmentFilter = fulfilmentFilterReactiveVar()

  // calculate the most appropriate preorder delivery window (null if asap or no windows available)
  const deliveryPreorderWindow = calculateDeliveryPreorderWindow({
    outlet,
    fulfilmentFilterWhen: fulfilmentFilter.when,
  })
  // calculate the most appropriate preorder datetime (null if asap or no datetimes available)
  const collectionPreorderDatetime = calculateCollectionPreorderDateTime({
    outlet,
    fulfilmentFilterWhen: fulfilmentFilter.when,
  })

  const historicalData = {
    collectionPreorderDatetime,
    deliveryPreorderWindow,
    tableId: null,
    tableNotes: null,
    ageVerificationConfirmed: false,
    deliveryNotes: null,
    collectionNotes: null,
    outlet,
    fulfilmentId: null,
    orderNotes: null,
    previousFulfilmentType: null,
  }

  // attempt to use priority fulfilment method that is currently available to customer location
  // fallback to priority fulfilment method currently available
  // and if no methods are currently available fallback to generally available
  // (though ordering will be disabled anyway)
  const priorityFulfilmentMethod =
    outlet.orderability.prioritisedCurrentlyLocationAvailableFulfilmentMethods.find(
      method =>
        fulfilmentFilterData.data.nonEmptyFulfilmentMethods.includes(method)
    ) ||
    outlet.orderability.prioritisedCurrentlyAvailableFulfilmentMethods[0] ||
    outlet.orderability.prioritisedGenerallyAvailableFulfilmentMethods[0] ||
    NarrowFulfilmentMethodInputType.DELIVERY

  // Priority is DELIVERY, return object with relevant fields for the specific delivery location type
  if (priorityFulfilmentMethod === NarrowFulfilmentMethodInputType.DELIVERY) {
    switch (fulfilmentFilter.where.location.type) {
      case LocationType.EVERYWHERE:
        return {
          outletId: outlet.id,
          currentFulfilment: {
            type: CurrentFulfilmentType.DELIVERY_EVERYWHERE,
            deliveryPreorderWindow,
          },
          historicalData,
        }
      case LocationType.POSTCODE:
        return {
          outletId: outlet.id,
          currentFulfilment: {
            type: CurrentFulfilmentType.DELIVERY_POSTCODE,
            deliveryPreorderWindow,
            postAndCountryCode:
              fulfilmentFilter.where.location.postAndCountryCode,
          },
          historicalData,
        }
      case LocationType.COORDINATES:
        return {
          outletId: outlet.id,
          currentFulfilment: {
            type: CurrentFulfilmentType.DELIVERY_COORDINATES,
            deliveryPreorderWindow,
            coordinates: fulfilmentFilter.where.location.coordinates,
          },
          historicalData,
        }
      case LocationType.ADDRESS:
        return {
          outletId: outlet.id,
          currentFulfilment: {
            type: CurrentFulfilmentType.DELIVERY_SAVED_ADDRESS,
            deliveryPreorderWindow,
            addressId: fulfilmentFilter.where.location.addressId,
            deliveryNotes: null,
            fulfilmentId: null,
          },
          historicalData,
        }
      case LocationType.DELIVERY_ZONE:
        return {
          outletId: outlet.id,
          currentFulfilment: {
            type: CurrentFulfilmentType.DELIVERY_ZONE,
            deliveryPreorderWindow,
            zoneId: fulfilmentFilter.where.location.zoneId,
            deliveryNotes: null,
          },
          historicalData,
        }
      case LocationType.PLACE_SEARCH:
        return {
          outletId: outlet.id,
          currentFulfilment: {
            type: CurrentFulfilmentType.DELIVERY_PLACE_SEARCH,
            deliveryPreorderWindow,
            address: fulfilmentFilter.where.location.address,
            placeId: fulfilmentFilter.where.location.placeId,
            deliveryNotes: null,
          },
          historicalData,
        }
    }
  }
  // Priority is COLLECTION, return object matching the single collection fulfilment type
  if (priorityFulfilmentMethod === NarrowFulfilmentMethodInputType.COLLECTION) {
    return {
      outletId: outlet.id,
      currentFulfilment: {
        type: CurrentFulfilmentType.COLLECTION,
        collectionPreorderDatetime,
        collectionNotes: null,
      },
      historicalData,
    }
  }

  // Priority is TABLE, return object matching the single table fulfilment type
  if (priorityFulfilmentMethod === NarrowFulfilmentMethodInputType.TABLE) {
    return {
      outletId: outlet.id,
      currentFulfilment: {
        type: CurrentFulfilmentType.TABLE_UNSELECTED,
        tableNotes: null,
      },
      historicalData,
    }
  }

  // default return, should never be hit
  return {
    outletId: outlet.id,
    currentFulfilment: {
      type: CurrentFulfilmentType.DELIVERY_EVERYWHERE,
      deliveryPreorderWindow,
    },
    historicalData,
  }
}
